import { Injectable } from '@angular/core';
import { icons, LucideIconData } from 'lucide-angular';

import { VIDEO_EFFECTS_ICON } from '../../icons/icon.component';

/**
 * Enterprise-grade icon service for managing Lucide icons
 * Automatically resolves any Lucide icon name with fallback support
 */
@Injectable({
  providedIn: 'root',
})
export class IconService {
  private iconRegistry = new Map<string, LucideIconData>();
  private readonly fallbackIconName = 'CircleAlert';

  constructor() {
    this.registerIcons();
  }

  /**
   * Register all available Lucide icons in the registry
   */
  private registerIcons(): void {
    // Register all lucide icons with multiple key formats for flexibility
    Object.entries(icons).forEach(([name, iconData]) => {
      // Store with original PascalCase name
      this.iconRegistry.set(name, iconData);
      // Store with lowercase version
      this.iconRegistry.set(name.toLowerCase(), iconData);
      // Store with kebab-case version
      this.iconRegistry.set(this.toKebabCase(name), iconData);
    });
    // Glyphs Lucide has no single icon for, composed from ones it does. Shared
    // with the app-icon set so the same name draws the same mark in both.
    for (const alias of ['VideoEffects', 'videoeffects', 'video-effects']) {
      this.iconRegistry.set(alias, VIDEO_EFFECTS_ICON);
    }
  }

  /**
   * Convert PascalCase to kebab-case
   * ChevronDown -> chevron-down
   */
  private toKebabCase(name: string): string {
    return name
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '');
  }

  /**
   * Get icon data by name with automatic fallback
   * @param name - Icon name (PascalCase, camelCase, or kebab-case)
   * @returns LucideIconData - Icon data or fallback icon if not found
   */
  getIcon(name: string): LucideIconData {
    if (!name) {
      return this.getFallbackIcon();
    }

    // Try to get the icon with the exact name first
    let icon = this.iconRegistry.get(name);
    
    if (!icon) {
      // Try lowercase
      icon = this.iconRegistry.get(name.toLowerCase());
    }
    
    if (!icon) {
      // Try kebab-case
      icon = this.iconRegistry.get(this.toKebabCase(name));
    }
    
    if (!icon) {
      console.warn(`Icon "${name}" not found in Lucide icons. Using fallback icon.`);
      return this.getFallbackIcon();
    }

    return icon;
  }

  /**
   * Get the fallback icon
   */
  private getFallbackIcon(): LucideIconData {
    return this.iconRegistry.get(this.fallbackIconName) || icons.CircleAlert || icons.Circle;
  }

  /**
   * Check if an icon exists in the registry
   */
  hasIcon(name: string): boolean {
    return (
      this.iconRegistry.has(name) ||
      this.iconRegistry.has(name.toLowerCase()) ||
      this.iconRegistry.has(this.toKebabCase(name))
    );
  }

  /**
   * Get all available icon names (in PascalCase format)
   */
  getAvailableIcons(): string[] {
    return Object.keys(icons).sort();
  }
}
